import {
  ALERT_COMPARATORS,
  ALERT_SIGNALS,
  ALERT_SIGNAL_LABELS,
  comparatorSymbol,
} from '@connaro/shared';
import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { NotificationChannelsPanel } from '../components/NotificationChannelsPanel';
import { Banner, EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface AlertRule {
  id: string;
  name: string;
  signal: string;
  comparator: string;
  threshold: number;
  severity: string;
  enabled: boolean;
}

interface MaintenanceWindow {
  id: string;
  reason: string;
  startsAt: string;
  endsAt: string;
}

const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;

export function AlertRulesPage(): JSX.Element {
  const t = useT();
  const rules = useAsync(() => api.get<AlertRule[]>('/alert-rules'), []);
  const windows = useAsync(() => api.get<MaintenanceWindow[]>('/maintenance-windows'), []);
  const [error, setError] = useState<string | null>(null);
  const [evalResult, setEvalResult] = useState<string | null>(null);

  // Rule form.
  const [name, setName] = useState('');
  const [signal, setSignal] = useState<string>(ALERT_SIGNALS[0]);
  const [comparator, setComparator] = useState<string>('GT');
  const [threshold, setThreshold] = useState('0');
  const [severity, setSeverity] = useState<string>('WARNING');

  // Window form.
  const [reason, setReason] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const addRule = (e: React.FormEvent): void => {
    e.preventDefault();
    void run(async () => {
      await api.post('/alert-rules', {
        name,
        signal,
        comparator,
        threshold: Number(threshold),
        severity,
      });
      setName('');
      rules.reload();
    });
  };

  const toggle = (rule: AlertRule): void =>
    void run(async () => {
      await api.patch(`/alert-rules/${rule.id}`, { enabled: !rule.enabled });
      rules.reload();
    });

  const removeRule = (id: string): void =>
    void run(async () => {
      await api.del(`/alert-rules/${id}`);
      rules.reload();
    });

  const evaluateNow = (): void =>
    void run(async () => {
      const res = await api.post<{ opened: number; resolved: number; suppressed: boolean }>(
        '/alert-rules/evaluate',
      );
      setEvalResult(
        t('alertRules.evalResult')
          .replace('{opened}', String(res.opened))
          .replace('{resolved}', String(res.resolved)) +
          (res.suppressed ? ` · ${t('alertRules.suppressed')}` : ''),
      );
    });

  const addWindow = (e: React.FormEvent): void => {
    e.preventDefault();
    void run(async () => {
      await api.post('/maintenance-windows', {
        reason,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
      });
      setReason('');
      setStartsAt('');
      setEndsAt('');
      windows.reload();
    });
  };

  const removeWindow = (id: string): void =>
    void run(async () => {
      await api.del(`/maintenance-windows/${id}`);
      windows.reload();
    });

  return (
    <section>
      <div className="page-head">
        <h1>{t('alertRules.title')}</h1>
        <button type="button" className="btn" onClick={evaluateNow}>
          {t('alertRules.evaluateNow')}
        </button>
      </div>
      {error ? <Banner variant="error">{error}</Banner> : null}
      {evalResult ? <Banner variant="info">{evalResult}</Banner> : null}

      <div className="card">
        <h2>{t('alertRules.rules')}</h2>
        <form className="form form--inline" onSubmit={addRule}>
          <label className="field">
            <span>{t('alertRules.name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span>{t('alertRules.signal')}</span>
            <select value={signal} onChange={(e) => setSignal(e.target.value)}>
              {ALERT_SIGNALS.map((s) => (
                <option key={s} value={s}>
                  {ALERT_SIGNAL_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('alertRules.comparator')}</span>
            <select value={comparator} onChange={(e) => setComparator(e.target.value)}>
              {ALERT_COMPARATORS.map((c) => (
                <option key={c} value={c}>
                  {comparatorSymbol(c)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('alertRules.threshold')}</span>
            <input
              inputMode="numeric"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </label>
          <label className="field">
            <span>{t('alertRules.severity')}</span>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn">
            {t('alertRules.add')}
          </button>
        </form>

        {rules.loading ? (
          <Loading />
        ) : rules.error ? (
          <ErrorState message={rules.error.message} onRetry={rules.reload} />
        ) : !rules.data || rules.data.length === 0 ? (
          <EmptyState hint={t('alertRules.noRules')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('alertRules.name')}</th>
                  <th>{t('alertRules.condition')}</th>
                  <th>{t('alertRules.severity')}</th>
                  <th>{t('alertRules.enabled')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {rules.data.map((rule) => (
                  <tr key={rule.id}>
                    <td>{rule.name}</td>
                    <td>
                      {ALERT_SIGNAL_LABELS[rule.signal as keyof typeof ALERT_SIGNAL_LABELS] ??
                        rule.signal}{' '}
                      {comparatorSymbol(rule.comparator as never)} {rule.threshold}
                    </td>
                    <td>
                      <span className={`chip chip--${rule.severity.toLowerCase()}`}>
                        {rule.severity}
                      </span>
                    </td>
                    <td>{rule.enabled ? '✓' : '—'}</td>
                    <td className="row-actions">
                      <button type="button" className="btn btn--ghost" onClick={() => toggle(rule)}>
                        {rule.enabled ? t('alertRules.disable') : t('alertRules.enable')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--danger"
                        onClick={() => removeRule(rule.id)}
                      >
                        {t('alertRules.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>{t('alertRules.windows')}</h2>
        <p>{t('alertRules.windowsIntro')}</p>
        <form className="form form--inline" onSubmit={addWindow}>
          <label className="field">
            <span>{t('alertRules.reason')}</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('alertRules.startsAt')}</span>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>{t('alertRules.endsAt')}</span>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn">
            {t('alertRules.addWindow')}
          </button>
        </form>

        {windows.loading ? (
          <Loading />
        ) : !windows.data || windows.data.length === 0 ? (
          <EmptyState hint={t('alertRules.noWindows')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('alertRules.reason')}</th>
                  <th>{t('alertRules.startsAt')}</th>
                  <th>{t('alertRules.endsAt')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {windows.data.map((w) => (
                  <tr key={w.id}>
                    <td>{w.reason || '—'}</td>
                    <td>{new Date(w.startsAt).toLocaleString()}</td>
                    <td>{new Date(w.endsAt).toLocaleString()}</td>
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--danger"
                        onClick={() => removeWindow(w.id)}
                      >
                        {t('alertRules.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NotificationChannelsPanel />
    </section>
  );
}
